import { useCallback, useRef, useState } from 'react';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { toast } from '../../../util/toasts';
import { PasswordRequirements } from '../../../clients/quipli-service/admin/admin';
import { debounce } from 'lodash';

export interface UseAdminOperationsReturn {
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isListing: boolean;

  // CRUD operations
  createUser: (userData: {
    adminUsername: string;
    adminEmail: string;
    adminFullName: string;
    adminPassword: string;
  }) => Promise<Admin | null>;

  updateUser: (
    adminId: number,
    userData: {
      adminUsername: string;
      adminEmail: string;
      adminFullName: string;
      adminPassword?: string;
    },
  ) => Promise<Admin | null>;

  deleteUser: (adminId: number) => Promise<void>;

  updateLoggedProfile: (userData: {
    adminUsername: string;
    adminEmail: string;
    adminFullName: string;
    currentPassword?: string;
    adminPassword?: string;
  }) => Promise<Admin | null>;

  fetchPasswordRequirements: () => Promise<PasswordRequirements | null>;

  // Listing operations
  listUsers: (setAdmins: (admins: Admin[]) => void) => void;
}

export const useAdminOperations = (): UseAdminOperationsReturn => {
  const {
    createUserAdmin,
    updateUserAdmin,
    updateAdminLoggedProfile,
    deleteAdmin,
    getPasswordRequirements,
    getAdminsList,
  } = useAdminApiV1();

  // Loading states
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isListing, setIsListing] = useState<boolean>(true);

  // Common error handling wrapper
  const withErrorHandling = async <T>(
    operation: () => Promise<T>,
    defaultErrorMessage: string,
    successMessage?: string,
  ): Promise<T | null> => {
    try {
      const result = await operation();
      if (successMessage) {
        toast(successMessage, 'success');
      }
      return result;
    } catch (err: any) {
      const errMsg = err.status === 400 && err.message ? err.message : defaultErrorMessage;
      toast(errMsg, 'error');
      return null;
    }
  };

  const createUser = async (userData: {
    adminUsername: string;
    adminEmail: string;
    adminFullName: string;
    adminPassword: string;
  }): Promise<Admin | null> => {
    setIsCreating(true);
    try {
      return await withErrorHandling(
        async () => {
          return await createUserAdmin(userData);
        },
        'Error creating admin user',
        'Admin user created successfully',
      );
    } finally {
      setIsCreating(false);
    }
  };

  const updateUser = async (
    adminId: number,
    userData: {
      adminUsername: string;
      adminEmail: string;
      adminFullName: string;
      adminPassword?: string;
    },
  ): Promise<Admin | null> => {
    setIsUpdating(true);
    try {
      return await withErrorHandling(
        async () => {
          return await updateUserAdmin(adminId, {
            adminId,
            ...userData,
          });
        },
        'Error updating admin user details',
        'Admin user details updated successfully',
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteUser = async (adminId: number): Promise<void> => {
    setIsDeleting(true);
    try {
      await withErrorHandling(
        async () => {
          return await deleteAdmin(adminId);
        },
        'Error removing user',
        'User removed successfully',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const updateLoggedProfile = async (userData: {
    adminUsername: string;
    adminEmail: string;
    adminFullName: string;
    currentPassword?: string;
    adminPassword?: string;
  }): Promise<Admin | null> => {
    setIsUpdating(true);
    try {
      return await withErrorHandling(
        async () => {
          return await updateAdminLoggedProfile(userData);
        },
        'Error updating profile',
        'Profile updated successfully',
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchPasswordRequirements = async (): Promise<PasswordRequirements | null> => {
    return await withErrorHandling(async () => {
      return await getPasswordRequirements();
    }, 'Error fetching password requirements');
  };

  // Debounced fetcher (internal)
  const debouncedFetch = useRef(
    debounce(async (resolve: (admins: Admin[] | null) => void) => {
      setIsListing(true);
      const admins = await withErrorHandling(async () => await getAdminsList(), 'Error listing admin users');
      setIsListing(false);
      resolve(admins);
    }, 600),
  ).current;

  const listUsers = useCallback(
    (setAdmins: (admins: Admin[]) => void) => {
      const promise = new Promise<Admin[] | null>((resolve) => {
        debouncedFetch(resolve);
      });
      promise.then((admins) => {
        setAdmins(admins || []);
      });
    },
    [debouncedFetch],
  );

  return {
    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isListing,

    // Listing operations
    listUsers,

    // CRUD operations
    createUser,
    updateUser,
    deleteUser,
    updateLoggedProfile,
    fetchPasswordRequirements,
  };
};
